/**
 * Sentry initialization + release tagging.
 * monitoringSKILL Rules 3, 7, 9, 10.
 * - COPPA: beforeSend strips ALL PII before any event leaves the browser
 * - Replay is permanently DISABLED (COPPA D-013 — session recording)
 * - Release tagged with git SHA in CI — never hardcoded (Rule 7)
 * Call initSentry() once at top of main.tsx, before anything else renders.
 */
import { useEffect } from 'react'
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom'
import * as Sentry from '@sentry/react'

export function initSentry(): void {
  // ── Production-only init (June 12, 2026 incident) ───────────────────────────
  // VITE_SENTRY_DSN lives in .env.local, so localhost dev sessions were sending
  // environment=development events that triggered the team's alert rules during
  // preview testing. Sentry only initializes on real production builds:
  //  - import.meta.env.DEV        → localhost `vite dev` — skip (the incident)
  //  - no DSN                     → nothing to send — skip (explicit no-op,
  //                                 mirrors initServerSentry in api/generate-ai.ts)
  //  - VITE_VERCEL_ENV='preview'  → Vercel preview deploys build in production
  //                                 mode, so MODE can't distinguish them — skip
  //                                 to keep preview noise out of prod alerts.
  //                                 (Vercel system env, auto-exposed for Vite;
  //                                 undefined locally and on prod = fail-safe:
  //                                 production always initializes.)
  if (import.meta.env.DEV) return
  if (!import.meta.env.VITE_SENTRY_DSN) return
  if (import.meta.env.VITE_VERCEL_ENV === 'preview') return

  Sentry.init({
    dsn:         import.meta.env.VITE_SENTRY_DSN,
    // Align with the server-side convention (api/generate-ai.ts uses
    // VERCEL_ENV): "production" | "preview" | "development"
    environment: import.meta.env.VITE_VERCEL_ENV ?? import.meta.env.MODE,
    release:     import.meta.env.VITE_SENTRY_RELEASE,   // CI-injected git SHA — see deploy.yml

    // Rule 8 — sample 10% of transactions in prod, 0% in dev to avoid noise
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0,

    // Drop errors from browser extensions — they're never our code
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],

    // COPPA Rule 3 — strip ALL PII before any event leaves the browser
    beforeSend(event) {
      if (event.user) {
        delete event.user.email
        delete event.user.username
        delete event.user.ip_address
        // Keep: event.user.id (opaque UUID only — not PII)
      }
      // Strip request body — may contain student text
      if (event.request?.data)    delete event.request.data
      if (event.request?.cookies) delete event.request.cookies

      // Drop network errors when the device is already offline — expected, not actionable
      if (!navigator.onLine) {
        const msg = event.exception?.values?.[0]?.value ?? ''
        if (/network|fetch|failed to fetch|load failed|the internet connection appears to be offline/i.test(msg)) {
          return null
        }
      }

      // Drop ResizeObserver errors that slip past ignoreErrors (wording varies by browser)
      const topMsg = event.exception?.values?.[0]?.value ?? ''
      if (/resizeobserver/i.test(topMsg)) return null

      return event
    },

    // Ignore noisy browser errors that are not actionable.
    // Note: beforeSend above handles the runtime-state-dependent variants
    // (offline network errors, ResizeObserver wording variations).
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Network request failed',
      'NetworkError when attempting to fetch resource',
      'Load failed',
      'Failed to fetch',
      'AbortError',
      'NotAllowedError',    // iOS PWA permission dismissals
      'ChunkLoadError',     // Stale SW — auto-resolved on next visit
    ],

    // ── Sentry dashboard alert rules (Alerts → Alert Rules in Sentry UI) ────────
    // Set these up once after deploy. All 6 rules are required for full observability.
    //
    //  #  Rule                        | Threshold      | Window | Action
    //  ---|----------------------------|----------------|--------|----------------------------
    //  1  Error spike                 | > 50/min       | 1 min  | Notify on-call (email+Slack)
    //  2  New issue (first seen)      | count ≥ 1      | —      | Notify on-call
    //  3  High-frequency issue        | > 100/hr       | 1 hr   | Notify on-call
    //  4  Crash-free sessions drop    | < 99.5%        | 1 hr   | Notify on-call (Releases)
    //  5  P95 transaction latency     | > 3 000 ms     | 5 min  | Warn only
    //  6  ai_dual_failure (server)    | count ≥ 1      | 5 min  | Critical + page on-call
    //
    // Setup instructions (one-time, in Sentry UI):
    //
    //  Rules 1-5: Alerts → Alert Rules → "Create Alert" → select metric/issue type → enter threshold.
    //    Rule 4 needs "Releases" enabled (Project Settings → Releases → enable crash-free sessions).
    //    Rule 5: Performance → select "p95(transaction.duration)" metric → set threshold 3000ms.
    //
    //  Rule 6 (ai_dual_failure server-side):
    //    Source: api/generate-ai.ts fires SentryNode.captureMessage('ai_dual_failure', 'error')
    //    when both Gemini and Claude Haiku fail on the same request (extremely rare — means GCP +
    //    Anthropic are simultaneously down or misconfigured).
    //    Setup: Alerts → Alert Rules → "Create Alert" → "Issues" type → filter by
    //    message = "ai_dual_failure" → threshold: count ≥ 1 in 5 min → action: page on-call.
    //    Tags available for triage: action (generate|vocab-definition), gemini_code, claude_code, reqId.
    //    Requires SENTRY_DSN set in Vercel env vars (server-side, no VITE_ prefix).
    //

    integrations: [
      // React Router v7 routing instrumentation (react-router-dom@^7)
      // Provides per-route transaction names in Sentry performance dashboard
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Replay permanently DISABLED — COPPA D-013
      // Session recording would capture student question/answer text
    ],
  })
}

// ── Scope helpers ─────────────────────────────────────────────────────────────

/** Call after successful auth — opaque userId + role only (no PII) */
export function setSentryUser(userId: string, role: 'student' | 'parent'): void {
  Sentry.setUser({ id: userId })
  Sentry.setTag('role', role)
}

/** Call on sign-out */
export function clearSentryUser(): void {
  Sentry.setUser(null)
}

/** Tag current grade level for triage grouping */
export function setSentryGrade(grade: number): void {
  Sentry.setTag('grade_level', grade)
}

/**
 * Rule 10 — log PII redaction COUNT (not content) on every Gemini call.
 * Lets us audit that stripping is working in production without logging what was stripped.
 */
export function logPiiRedaction(count: number, subject: string): void {
  if (count > 0) {
    Sentry.addBreadcrumb({
      category: 'pii',
      message:  'PII stripped from Gemini payload',
      level:    'info',
      data:     { redaction_count: count, subject },
    })
  }
}

// ── Router instrumentation ────────────────────────────────────────────────────

/**
 * Wraps RouterProvider with Sentry routing instrumentation.
 * Usage in main.tsx: wrap <RouterProvider> with this.
 * React Router v7 variant — matches react-router-dom@^7.
 */
export { Sentry }
export const withSentryRouting = Sentry.withSentryReactRouterV7Routing
